import { Link } from "react-router-dom";

import type { InAppNotification } from "@/features/notifications/api/inAppNotificationsApi";
import { metaForInAppSlug } from "@/features/notifications/lib/inAppNotificationMeta";
import { routeForNotificationSlug } from "@/features/notifications/lib/routes";

import { cn } from "@/shared/lib/cn";
import { formatRelative } from "@/shared/lib/format";

interface InAppNotificationRowProps {
  notification: InAppNotification;
  onOpen: () => void;
}

export function InAppNotificationRow({ notification: n, onOpen }: InAppNotificationRowProps) {
  const to = routeForNotificationSlug(n.template_slug);
  const { category, icon: Icon } = metaForInAppSlug(n.template_slug);
  const unread = !n.read_at;

  const className = cn(
    "flex gap-3 border-b border-border/50 px-4 py-3 last:border-b-0",
    "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/25",
  );

  const inner = (
    <>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-4" strokeWidth={1.75} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-xs text-foreground",
            unread ? "font-semibold" : "font-medium",
          )}
        >
          {n.subject}
        </span>
        {n.body ? (
          <span className="mt-0.5 block line-clamp-2 text-2xs leading-snug text-muted-foreground">
            {n.body}
          </span>
        ) : null}
        <span className="mt-1.5 inline-block rounded-md bg-muted/80 px-1.5 py-0.5 text-3xs font-medium text-muted-foreground">
          {category}
        </span>
      </span>
      {n.sent_at ? (
        <time
          dateTime={n.sent_at}
          className="shrink-0 self-start text-3xs text-muted-foreground"
        >
          {formatRelative(n.sent_at)}
        </time>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onOpen}
        data-testid="notification-bell-row"
        data-template-slug={n.template_slug ?? ""}
        className={className}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="notification-bell-row"
      data-template-slug={n.template_slug ?? ""}
      className={cn(className, "w-full text-left")}
    >
      {inner}
    </button>
  );
}
